// Copyright (c) 2026 Goffy. All Rights Reserved. This file belongs to Goffy.

import { getApiBaseUrl } from '@/services/apiBaseUrl';

/**
 * The prod Kibana as config/ports/ports.json registers it: Caddy maps this host to the
 * `kibana` entry's prod tunnel (local 5683 -> prod Elasticsearch).
 */
const PROD_KIBANA_BASE_URL = 'http://kibana-prod.goffy.localhost';

/**
 * Kibana's built-in all-logs data view, which spans the OTel log indices. The older
 * `logs-generic-default` view does not: a Kibana whose copy of it predates the otel indices
 * resolves the link to zero hits with no error.
 */
const PROD_DATA_VIEW_ID = 'discover-observability-solution-all-logs';

/**
 * The dev stack's own Kibana (`kibana` in config/ports/ports.json, local port 5681) and the
 * data view scripts/start_infra.py creates in it.
 *
 * A worker built from the working tree ships its logs and its analyser's to the LOCAL
 * collector — `docker-compose.dev.yml` defaults OTLP__Endpoint to the dev stack's Envoy —
 * so a job it ran is in the local Elasticsearch and nowhere near prod's. Which Kibana a job
 * link belongs to therefore follows the stack this app is talking to, not a fixed default:
 * the local admin pages open local logs, a build pointed at backend.goffy.app opens prod's.
 *
 * The exception the defaults cannot see is a worker configured the other way round — a local
 * stack with OTLP_ENDPOINT set to the prod edge, or a released one pointed at the dev
 * collector. Set EXPO_PUBLIC_KIBANA_BASE_URL (and EXPO_PUBLIC_KIBANA_DATA_VIEW_ID with it)
 * for that fleet; both override everything below.
 */
const DEV_KIBANA_BASE_URL = 'http://localhost:5681';

const DEV_DATA_VIEW_ID = 'logs-generic-default';

/** How far back Discover opens. A job running longer than this is its own alarm. */
const LOOKBACK = 'now-24h';

/** Which service said it, and what it said — the two columns worth opening on. */
const COLUMNS = ['resource.attributes.service.name', 'body.text'];

/**
 * The job id under whichever attribute logged it: course analysis logs {RunId}, flyover and
 * ML log {JobId}, and body.text catches whoever only names it in the message text.
 */
const jobQuery = (jobId: string): string =>
  `attributes.RunId:"${jobId}" or attributes.JobId:"${jobId}" or body.text:"${jobId}"`;

/**
 * Escape one rison state for the URL hash. `:` `,` and `@` are structural in rison and legal
 * in a fragment, so they are put back after encoding — Kibana reads either form, but leaving
 * them escaped turns the link into a wall of %3A that nobody can read or hand-edit. Anything
 * that would break the hash ('&', '#', quotes, spaces) stays encoded.
 */
const risonParam = (state: string): string =>
  encodeURIComponent(state).replace(/%3A/g, ':').replace(/%2C/g, ',').replace(/%40/g, '@');

/**
 * Whether this app talks to the prod backend. Anything else — a localhost origin, a LAN
 * address on a phone, an explicit EXPO_PUBLIC_API_BASE_URL — is a developer's stack, whose
 * workers log to the dev collector.
 */
export const isProdBackend = (): boolean => /^https?:\/\/[^/]*\bgoffy\.app\b/i.test(getApiBaseUrl());

/** Base URL of the Kibana to link to, without a trailing slash. */
export const getKibanaBaseUrl = (): string =>
  (process.env.EXPO_PUBLIC_KIBANA_BASE_URL
    || (isProdBackend() ? PROD_KIBANA_BASE_URL : DEV_KIBANA_BASE_URL)).replace(/\/+$/, '');

/** The data view that Kibana's logs live in — the local one names its own. */
export const getKibanaDataViewId = (): string =>
  process.env.EXPO_PUBLIC_KIBANA_DATA_VIEW_ID
  || (isProdBackend() ? PROD_DATA_VIEW_ID : DEV_DATA_VIEW_ID);

export interface DiscoverView {
  /** KQL, unescaped — the builder escapes it for the hash. */
  query: string;
  /** Start of the time range, in Kibana's relative form (`now-24h`). Ends at `now`. */
  lookback: string;
  /** Columns to open on, in order. */
  columns: string[];
  /** Fixed widths for columns that would otherwise be sized by their widest value. */
  columnWidths?: Record<string, number>;
}

/**
 * A Kibana Discover link on the logs data view, newest line first.
 *
 * The app state uses the `dataSource:(dataViewId:…,type:dataView)` shape current Kibana
 * writes; the older `index:'…'` key it replaced is ignored on arrival, which drops the link
 * on whatever data view the session last used rather than the log one. No `_tab` id is sent —
 * Kibana mints one per tab, and a fixed one would make every link reuse the same tab.
 */
export const buildDiscoverUrl = ({ query, lookback, columns, columnWidths = {} }: DiscoverView): string => {
  const dataViewId = getKibanaDataViewId();
  const globalState = `(filters:!(),refreshInterval:(pause:!t,value:60000),time:(from:${lookback},to:now))`;
  const widths = Object.entries(columnWidths).map(([column, width]) => `${column}:(width:${width})`);
  const grid = widths.length > 0 ? `grid:(columns:(${widths.join(',')})),` : '';
  const appState =
    `(columns:!(${columns.join(',')}),dataSource:(dataViewId:${dataViewId},type:dataView),filters:!(),` +
    `${grid}interval:auto,query:(language:kuery,query:'${query}'),sort:!(!('@timestamp',desc)))`;
  return `${getKibanaBaseUrl()}/app/discover#/?_g=${risonParam(globalState)}&_a=${risonParam(appState)}`;
};

/** A Kibana Discover link showing the last day of logs for one batch job. */
export const buildJobLogsUrl = (jobId: string): string =>
  buildDiscoverUrl({ query: jobQuery(jobId), lookback: LOOKBACK, columns: COLUMNS });
